import { logger } from '@/lib/logger';
import { sessionIdentity,syncSessionCacheIdentity } from '@/lib/sessionCache';
import type { AuthState,User } from '@shared/types';
import { create } from 'zustand';
import { createJSONStorage,persist } from 'zustand/middleware';

// ============================================
// Storage Strategy
// ============================================

/**
 * Tokens never touch persistent storage (OWASP: web storage is readable by
 * any script on the page, so one XSS leaks every token):
 *
 * - Access token: held in memory only, per tab. Short-lived (15 min).
 * - Refresh token: HttpOnly cookie set by the backend; invisible to JS.
 *   Persistent ("remember me", 7 days) or browser-session scoped.
 *
 * Only the non-sensitive user profile and an isAuthenticated hint are
 * persisted (localStorage, shared across tabs). On load, a tab that is
 * "authenticated" but has no access token silently refreshes via the cookie
 * (see api.restoreSession), matching standard session-cookie behavior.
 */
const AUTH_STORAGE_KEY = 'stockforge-auth';
const REMEMBER_ME_KEY = 'stockforge-auth-remember';
const LOGOUT_SYNC_KEY = 'stockforge-auth-logout';
const LOGOUT_BROADCAST_CHANNEL = 'stockforge-auth-events';

/**
 * The user's "remember me on this device" choice. Sent to the backend at
 * login to control refresh-cookie persistence. Stored so flows that finish
 * on another page (magic link verification) can honor it. Defaults to true.
 */
export const getRememberMe = (): boolean => {
  try {
    return window.localStorage.getItem(REMEMBER_ME_KEY) !== '0';
  } catch {
    return true;
  }
};

export const setRememberMe = (remember: boolean): void => {
  try {
    window.localStorage.setItem(REMEMBER_ME_KEY, remember ? '1' : '0');
  } catch {
    // Storage unavailable; the backend default (remembered) applies.
  }
};

const secureStorage = createJSONStorage(() => window.localStorage);

const clearPersistedAuth = (): void => {
  try {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // Ignore; localStorage may be unavailable.
  }
  // Remove any token-bearing state persisted by previous app versions.
  try {
    window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // Ignore.
  }
};

const getLoggedOutState = (): Pick<AuthState, 'user' | 'accessToken' | 'refreshToken' | 'isAuthenticated' | 'isLoading' | 'error'> => ({
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
});

const broadcastLogout = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if ('BroadcastChannel' in window) {
    const channel = new BroadcastChannel(LOGOUT_BROADCAST_CHANNEL);
    channel.postMessage({ type: 'logout' });
    channel.close();
  }

  try {
    window.localStorage.setItem(LOGOUT_SYNC_KEY, String(Date.now()));
  } catch {
    // Ignore storage failures; BroadcastChannel already covered modern browsers.
  }
};

/**
 * Sanitize user object to prevent XSS through stored user data
 */
const sanitizeUser = (user: User | null): User | null => {
  if (!user) return null;
  return {
    ...user,
    name: user.name ? String(user.name).slice(0, 200) : undefined,
    email: user.email ? String(user.email).slice(0, 254) : '',
    // Ensure role is a valid enum value
    role: ['ADMIN', 'MANAGER', 'OPERATOR', 'VIEWER'].includes(user.role)
      ? user.role
      : 'VIEWER',
  } as User;
};

/**
 * Validate token format (basic JWT structure check)
 */
const isValidTokenFormat = (token: string | null): boolean => {
  if (!token) return false;
  // JWT should have 3 parts separated by dots
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  // Each part should be base64url encoded (alphanumeric, -, _)
  const base64urlRegex = /^[A-Za-z0-9_-]+$/;
  return parts.every(part => base64urlRegex.test(part));
};

// ============================================
// Auth Store Interface
// ============================================

interface AuthStore extends AuthState {
  login: (user: User, accessToken: string) => void;
  logout: () => void;
  /** Clear auth state in this tab only, without broadcasting to other tabs. */
  clearLocalSession: () => void;
  setUser: (user: User) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  updateAccessToken: (accessToken: string) => void;
  clearSensitiveData: () => void;
  setOnboardingCompleted: (completed: boolean) => void;
  setEmailVerified: (verified: boolean) => void;
}

// ============================================
// Auth Store
// ============================================

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: (user, accessToken) => {
        // Validate token format before storing
        if (!isValidTokenFormat(accessToken)) {
          logger.error('Invalid token format received');
          set({ error: 'Authentication failed - invalid token format', isLoading: false });
          return;
        }

        const nextUser = sanitizeUser(user);
        syncSessionCacheIdentity(sessionIdentity(true, nextUser?.id, nextUser?.organizationId));
        set({
          user: nextUser,
          accessToken,
          refreshToken: null,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      },

      logout: () => {
        syncSessionCacheIdentity(sessionIdentity(false, null));
        // Clear all sensitive data
        set(getLoggedOutState());
        // Clear storage explicitly
        clearPersistedAuth();
        broadcastLogout();
      },

      clearLocalSession: () => {
        syncSessionCacheIdentity(sessionIdentity(false, null));
        set(getLoggedOutState());
        clearPersistedAuth();
      },

      setUser: (user) => {
        const nextUser = sanitizeUser(user);
        syncSessionCacheIdentity(sessionIdentity(get().isAuthenticated, nextUser?.id, nextUser?.organizationId));
        set({ user: nextUser });
      },

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error: error ? String(error).slice(0, 500) : null, isLoading: false }),

      updateAccessToken: (accessToken) => {
        // Validate token format before storing
        if (!isValidTokenFormat(accessToken)) {
          logger.error('Invalid token format received during refresh');
          get().logout();
          return;
        }
        set({ accessToken });
      },

      clearSensitiveData: () => {
        set({
          accessToken: null,
          refreshToken: null,
        });
      },

      setOnboardingCompleted: (completed) => {
        const user = get().user;
        if (user) {
          set({ user: { ...user, onboardingCompleted: completed } });
        }
      },

      setEmailVerified: (verified) => {
        const user = get().user;
        if (user) {
          set({ user: { ...user, emailVerified: verified } });
        }
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: secureStorage,
      partialize: (state) => ({
        // Persist only non-sensitive user info plus the authenticated hint.
        // Tokens are intentionally excluded: the access token lives in
        // memory and the refresh token in an HttpOnly cookie.
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      merge: (persistedState, currentState) => {
        const persisted = (persistedState ?? {}) as Partial<AuthStore>;
        const nextAuth = persisted.isAuthenticated ?? false;
        const nextUser = persisted.user ?? null;
        const nextIdentity = sessionIdentity(nextAuth, nextUser?.id, nextUser?.organizationId);
        const previousIdentity = sessionIdentity(
          currentState.isAuthenticated,
          currentState.user?.id,
          currentState.user?.organizationId,
        );
        const changed = previousIdentity !== nextIdentity;
        if (changed) {
          syncSessionCacheIdentity(nextIdentity);
        }
        return {
          ...currentState,
          ...persisted,
          ...(changed ? { accessToken: null, refreshToken: null } : {}),
        };
      },
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          logger.error('Error rehydrating auth store:', error);
          state?.logout();
        }
      },
    }
  )
);

// ============================================
// Security Event Listeners
// ============================================

if (typeof window !== 'undefined') {
  const syncLogout = () => {
    useAuthStore.getState().clearLocalSession();
  };

  if ('BroadcastChannel' in window) {
    const channel = new BroadcastChannel(LOGOUT_BROADCAST_CHANNEL);
    channel.addEventListener('message', (event) => {
      if (event.data?.type === 'logout') {
        syncLogout();
      }
    });
  }

  // Listen for localStorage ping events from other tabs to sync logout
  window.addEventListener('storage', (event) => {
    if (event.key === LOGOUT_SYNC_KEY && event.newValue) {
      syncLogout();
    }
  });

  // Pick up a login performed in another tab so open login screens become
  // authenticated without a reload (this tab then silently refreshes its
  // own access token via the cookie).
  window.addEventListener('storage', (event) => {
    if (event.key === AUTH_STORAGE_KEY && event.newValue) {
      void useAuthStore.persist.rehydrate();
    }
  });
}
